import React from 'react';
import { Card as MuiCard, CardContent, CardHeader, CardActions, Box, IconButton } from '@mui/material';
import { styled } from '@mui/material/styles';
import { brandColors, designTokens } from '~/presentation/themes/modernTheme';
import { Close as CloseIcon } from '@mui/icons-material';

const StyledCard = styled(MuiCard)(({ theme, variant, elevated, interactive }) => ({
  borderRadius: designTokens.borderRadius.lg,
  border: `1px solid ${brandColors.neutral[100]}`,
  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  
  ...(variant === 'outlined' && {
    boxShadow: 'none',
    border: `2px solid ${brandColors.neutral[200]}`,
  }),
  
  ...(variant === 'filled' && {
    backgroundColor: brandColors.neutral[50],
    border: 'none',
  }),
  
  ...(variant === 'gradient' && {
    background: `linear-gradient(135deg, ${brandColors.primary[500]} 0%, ${brandColors.primary[700]} 100%)`,
    color: 'white',
    border: 'none',
    '& .MuiCardHeader-title': {
      color: 'white',
    },
    '& .MuiCardHeader-subheader': {
      color: 'rgba(255, 255, 255, 0.8)',
    },
  }),
  
  ...(elevated && {
    boxShadow: designTokens.shadows.lg,
  }),
  
  ...(interactive && {
    cursor: 'pointer',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: designTokens.shadows.xl,
      borderColor: brandColors.primary[200],
    },
    '&:active': {
      transform: 'translateY(0)',
    },
  }),
}));

const StyledCardHeader = styled(CardHeader)(({ theme, variant }) => ({
  paddingBottom: designTokens.spacing.sm,
  
  '& .MuiCardHeader-title': {
    fontSize: '1.25rem',
    fontWeight: designTokens.typography.fontWeights.semibold,
    lineHeight: 1.3,
    color: variant === 'gradient' ? 'white' : brandColors.neutral[900],
  },
  
  '& .MuiCardHeader-subheader': {
    fontSize: '0.875rem',
    fontWeight: designTokens.typography.fontWeights.regular,
    color: variant === 'gradient' ? 'rgba(255, 255, 255, 0.8)' : brandColors.neutral[600],
    marginTop: designTokens.spacing.xs,
  },
}));

const StyledCardContent = styled(CardContent)(({ theme, noPadding }) => ({
  padding: noPadding ? 0 : designTokens.spacing.lg,
  '&:last-child': {
    paddingBottom: noPadding ? 0 : designTokens.spacing.lg,
  },
}));

const StyledCardActions = styled(CardActions)(({ theme, justify }) => ({
  padding: `0 ${designTokens.spacing.lg}px ${designTokens.spacing.lg}px`,
  gap: designTokens.spacing.sm,
  
  ...(justify === 'center' && {
    justifyContent: 'center',
  }),
  
  ...(justify === 'end' && {
    justifyContent: 'flex-end',
  }),
  
  ...(justify === 'between' && {
    justifyContent: 'space-between',
  }),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  position: 'absolute',
  top: designTokens.spacing.sm,
  right: designTokens.spacing.sm,
  width: 32,
  height: 32,
  backgroundColor: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(8px)',
  border: `1px solid ${brandColors.neutral[200]}`,
  '&:hover': {
    backgroundColor: 'rgba(255, 255, 255, 1)',
    transform: 'scale(1.05)',
  },
}));

const ModernCard = ({
  children,
  title,
  subtitle,
  actions,
  variant = 'default', // 'default' | 'outlined' | 'filled' | 'gradient'
  elevated = false,
  interactive = false,
  closable = false,
  onClose,
  noPadding = false,
  actionsJustify = 'start', // 'start' | 'center' | 'end' | 'between'
  className,
  sx,
  ...props
}) => {
  const hasHeader = title || subtitle;
  const hasActions = actions && actions.length > 0;

  return (
    <StyledCard
      variant={variant}
      elevated={elevated}
      interactive={interactive}
      className={`modern-card ${className || ''}`}
      sx={sx}
      {...props}
    >
      {closable && (
        <CloseButton onClick={onClose} size="small">
          <CloseIcon fontSize="small" />
        </CloseButton>
      )}
      
      {hasHeader && (
        <StyledCardHeader
          title={title}
          subheader={subtitle}
          variant={variant}
        />
      )}
      
      <StyledCardContent noPadding={noPadding}>
        {children}
      </StyledCardContent>
      
      {hasActions && (
        <StyledCardActions justify={actionsJustify}>
          {actions.map((action, index) => (
            <Box key={index}>
              {action}
            </Box>
          ))}
        </StyledCardActions>
      )}
    </StyledCard>
  );
};

// Componente de Card com loading state
export const ModernCardSkeleton = ({ lines = 3, hasHeader = true, hasActions = false }) => {
  return (
    <StyledCard>
      {hasHeader && (
        <StyledCardHeader>
          <Box className="animate-pulse-modern">
            <Box sx={{ 
              height: 20, 
              backgroundColor: brandColors.neutral[200], 
              borderRadius: designTokens.borderRadius.sm,
              mb: 1,
              width: '60%'
            }} />
            <Box sx={{ 
              height: 16, 
              backgroundColor: brandColors.neutral[200], 
              borderRadius: designTokens.borderRadius.sm,
              width: '40%'
            }} />
          </Box>
        </StyledCardHeader>
      )}
      
      <StyledCardContent>
        <Box className="animate-pulse-modern">
          {Array.from({ length: lines }).map((_, index) => (
            <Box
              key={index}
              sx={{
                height: 16,
                backgroundColor: brandColors.neutral[200],
                borderRadius: designTokens.borderRadius.sm,
                mb: index < lines - 1 ? 1 : 0,
                width: index === lines - 1 ? '70%' : '100%',
              }}
            />
          ))}
        </Box>
      </StyledCardContent>
      
      {hasActions && (
        <StyledCardActions>
          <Box className="animate-pulse-modern">
            <Box sx={{ 
              height: 36, 
              width: 80,
              backgroundColor: brandColors.neutral[200], 
              borderRadius: designTokens.borderRadius.md 
            }} />
          </Box>
        </StyledCardActions>
      )}
    </StyledCard>
  );
};

// Componente de Card com status
export const ModernStatusCard = ({ 
  status = 'default', // 'default' | 'success' | 'warning' | 'error' | 'info'
  children,
  ...props 
}) => {
  const statusColors = {
    success: brandColors.success[500],
    warning: brandColors.warning[500],
    error: brandColors.error[500],
    info: brandColors.primary[500],
    default: brandColors.neutral[300],
  };

  return (
    <StyledCard
      sx={{
        borderLeft: `4px solid ${statusColors[status]}`,
        ...props.sx,
      }}
      {...props}
    >
      {children}
    </StyledCard>
  );
};

export default ModernCard;